"use client";

import clsx from "clsx";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { usePrefersReducedMotion } from "@/components/redesign/ChatSequence";
import { MarketingVisualBackdrop } from "@/components/redesign/MarketingVisualBackdrop";

export type VoiceConversationTurn = {
  speaker: string;
  time: string;
  quote: string;
  isGail?: boolean;
  /** Bold phrases inside the quote, matched in order. */
  highlights?: string[];
};

type MarketingVoiceVisualProps = {
  turns: VoiceConversationTurn[];
  className?: string;
};

type TurnHoverHandlers = {
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onFocus: () => void;
  onBlur: () => void;
};

type TurnLayout = {
  /** Horizontal anchor across the visual, in % of width. */
  anchorX: number;
  side: "top" | "bottom";
};

type Segment = { start: number; end: number };
type Bar = {
  x: number;
  y: number;
  height: number;
  segmentIndex: number | null;
};

/** Waveform center line and the vertical distance to each card, in % of height. */
const WAVE_CENTER = 50;
const CONNECTOR_REACH = 16;

const BAR_COUNT = 120;
const VIEW_W = 400;
const VIEW_H = 80;
const BAR_PITCH = VIEW_W / BAR_COUNT;
const BAR_WIDTH = BAR_PITCH * 0.26;
const MAX_BAR_HEIGHT = 76;

/** How flat a speech burst sits before its turn card has appeared. */
const MUTED_SCALE = "scaleY(0.08)";

/** Half the card's max width plus an edge gutter, used to clamp placement. */
const CARD_HALF_WIDTH = "calc(min(12.5rem, 44vw) / 2 + 0.5rem)";

/** Pause between one turn card appearing and the next. */
const REVEAL_GAP_MS = 900;

/** Usable horizontal span (0–1) and silent gap between consecutive bursts. */
const SPAN_START = 0.07;
const SPAN_END = 0.93;
const SEGMENT_GAP = 0.035;

/**
 * Each speech burst's width is proportional to how much that turn says, with
 * quiet gaps between so the turns (Maximo → Gail → Maximo) read distinctly.
 */
function buildSegments(turns: VoiceConversationTurn[]): Segment[] {
  if (turns.length === 0) return [];

  const lengths = turns.map((turn) => Math.max(1, turn.quote.trim().length));
  const totalLength = lengths.reduce((sum, len) => sum + len, 0);
  const totalGap = SEGMENT_GAP * (turns.length - 1);
  const usable = SPAN_END - SPAN_START - totalGap;

  let cursor = SPAN_START;
  return lengths.map((len) => {
    const width = usable * (len / totalLength);
    const segment = { start: cursor, end: cursor + width };
    cursor = segment.end + SEGMENT_GAP;
    return segment;
  });
}

function roundSvgValue(value: number) {
  return Number(value.toFixed(3));
}

function stableNoise(index: number) {
  const hash = Math.imul(index + 1, 2_654_435_761) >>> 0;
  const mixed = (hash ^ (hash >>> 16)) >>> 0;
  return mixed / 0xffffffff;
}

function buildBars(segments: Segment[]) {
  const bars: Bar[] = [];
  for (let i = 0; i < BAR_COUNT; i += 1) {
    const t = i / (BAR_COUNT - 1);
    const noise = stableNoise(i);

    const segmentIndex = segments.findIndex((s) => t >= s.start && t <= s.end);
    const segment = segmentIndex === -1 ? null : segments[segmentIndex];

    let height: number;
    if (segment) {
      const localT = (t - segment.start) / (segment.end - segment.start);
      // Arch within the burst so each turn swells then tapers to silence.
      // The shallow exponent keeps most of the burst near full volume.
      const arch = Math.sin(Math.PI * localT) ** 0.55;
      height = Math.max(2.5, (0.7 + 0.3 * noise) * arch * MAX_BAR_HEIGHT);
    } else {
      // Faint baseline between turns.
      height = 2 + noise * 1.5;
    }

    const roundedHeight = roundSvgValue(height);
    bars.push({
      x: roundSvgValue(i * BAR_PITCH + (BAR_PITCH - BAR_WIDTH) / 2),
      y: roundSvgValue(VIEW_H / 2 - roundedHeight / 2),
      height: roundedHeight,
      segmentIndex: segment ? segmentIndex : null,
    });
  }
  return bars;
}

function highlightQuote(quote: string, highlights: string[] = []) {
  if (highlights.length === 0) {
    return quote;
  }

  let remaining = quote;
  const parts: ReactNode[] = [];
  let key = 0;

  for (const phrase of highlights) {
    const index = remaining.indexOf(phrase);
    if (index === -1) continue;

    if (index > 0) {
      parts.push(remaining.slice(0, index));
    }

    parts.push(
      <strong key={key} className="font-medium text-black">
        {phrase}
      </strong>,
    );
    key += 1;
    remaining = remaining.slice(index + phrase.length);
  }

  if (remaining) {
    parts.push(remaining);
  }

  return parts;
}

function barOpacity(segmentIndex: number | null, activeTurn: number | null) {
  if (activeTurn === null) return 0.85;
  return segmentIndex === activeTurn ? 1 : 0.4;
}

function barFill(
  segmentIndex: number | null,
  turns: VoiceConversationTurn[],
): string {
  if (segmentIndex === null) return "var(--color-voice-wave)";
  return turns[segmentIndex]?.isGail
    ? "var(--color-voice-wave-gail)"
    : "var(--color-voice-wave-user)";
}

function VoiceTurnCard({
  turn,
  handlers,
  className,
}: Readonly<{
  turn: VoiceConversationTurn;
  handlers: TurnHoverHandlers;
  className?: string;
}>) {
  return (
    <article
      className={clsx(
        "rounded-xl bg-white p-3 shadow-[0_8px_24px_-12px_rgba(15,40,80,0.35)] md:p-3.5",
        "cursor-default ring-1 ring-transparent transition-shadow duration-200 ease-out",
        turn.isGail
          ? "hover:ring-gail-primary/40 focus-within:ring-gail-primary/40"
          : "hover:ring-black/15 focus-within:ring-black/15",
        className,
      )}
      {...handlers}
    >
      <div className="mb-1.5 flex items-baseline justify-between gap-2 text-xs">
        <span
          className={clsx(
            "font-medium",
            turn.isGail ? "text-gail-primary" : "text-black/70",
          )}
        >
          {turn.speaker}
        </span>
        <span className="text-black/70">{turn.time}</span>
      </div>
      <p className="font-monument text-[0.6875rem] leading-snug text-black/80 md:text-xs">
        &ldquo;{highlightQuote(turn.quote, turn.highlights)}&rdquo;
      </p>
    </article>
  );
}

function VoiceWaveform({
  bars,
  turns,
  activeTurn,
  revealedTurns,
  className,
}: Readonly<{
  bars: ReturnType<typeof buildBars>;
  turns: VoiceConversationTurn[];
  activeTurn: number | null;
  /** Bursts up to this turn index have "started talking" and grow in. */
  revealedTurns: number;
  className?: string;
}>) {
  return (
    <svg
      viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
      preserveAspectRatio="none"
      className={className}
      aria-hidden
    >
      {bars.map((bar, index) => {
        const isSpeech = bar.segmentIndex !== null;
        const isTalking =
          bar.segmentIndex !== null && bar.segmentIndex < revealedTurns;
        const rect = (
          <rect
            key={isSpeech ? undefined : `bar-${bar.x.toFixed(2)}`}
            x={bar.x}
            y={bar.y}
            width={BAR_WIDTH}
            height={bar.height}
            rx={BAR_WIDTH / 2}
            fill={barFill(bar.segmentIndex, turns)}
            opacity={barOpacity(bar.segmentIndex, activeTurn)}
            className={clsx(
              "transition-opacity duration-200 ease-out",
              isSpeech && `animate-voice-talk-${(index % 3) + 1}`,
            )}
            style={
              isSpeech
                ? {
                    // Scale each bar around its own center (SVG defaults to
                    // the view-box origin, which would skew the wave).
                    transformBox: "fill-box",
                    // Random (but stable) phase and tempo per bar so the
                    // bars jitter independently, like a live audio meter.
                    animationDelay: `${roundSvgValue(-stableNoise(index + 31) * 1.6)}s`,
                    animationDuration: `${roundSvgValue(1.3 + stableNoise(index + 7) * 0.7)}s`,
                  }
                : undefined
            }
          />
        );

        if (!isSpeech) return rect;

        // The jitter animation owns the rect's transform, so the mute/grow
        // scale lives on a wrapper group. The whole burst swells together,
        // like the speaker's volume rising from silence.
        return (
          <g
            key={`bar-${bar.x.toFixed(2)}`}
            style={{
              transformBox: "fill-box",
              transformOrigin: "center",
              transform: isTalking ? "scaleY(1)" : MUTED_SCALE,
              transition: "transform 800ms cubic-bezier(0.16, 1, 0.3, 1)",
            }}
          >
            {rect}
          </g>
        );
      })}
    </svg>
  );
}

export function MarketingVoiceVisual({
  turns,
  className,
}: Readonly<MarketingVoiceVisualProps>) {
  const [activeTurn, setActiveTurn] = useState<number | null>(null);
  const [shown, setShown] = useState(0);
  const [inView, setInView] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const reducedMotion = usePrefersReducedMotion();

  // Arm once the visual scrolls into view, then reveal the turn cards one by
  // one. Cards stay mounted and toggle visibility classes only, so the
  // children set never changes during playback.
  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.35 },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!inView || reducedMotion || shown >= turns.length) return;
    const timeout = setTimeout(
      () => setShown((current) => current + 1),
      REVEAL_GAP_MS,
    );
    return () => clearTimeout(timeout);
  }, [inView, reducedMotion, shown, turns.length]);

  const isRevealed = (index: number) => reducedMotion || index < shown;
  const revealedTurns = reducedMotion ? turns.length : shown;

  const segments = useMemo(() => buildSegments(turns), [turns]);
  const bars = useMemo(() => buildBars(segments), [segments]);
  const turnLayout = useMemo<TurnLayout[]>(
    () =>
      segments.map((segment, index) => ({
        anchorX: ((segment.start + segment.end) / 2) * 100,
        side: index % 2 === 1 ? "bottom" : "top",
      })),
    [segments],
  );

  const handlersFor = (index: number): TurnHoverHandlers => ({
    onMouseEnter: () => setActiveTurn(index),
    onMouseLeave: () => setActiveTurn(null),
    onFocus: () => setActiveTurn(index),
    onBlur: () => setActiveTurn(null),
  });

  return (
    <div
      ref={containerRef}
      className={clsx(
        "relative w-full overflow-hidden rounded-2xl md:aspect-[7/6] md:rounded-3xl",
        className,
      )}
    >
      <MarketingVisualBackdrop />

      {/* Mobile: waveform header + vertical chat thread (cards would collide
          if anchored along the wave on a narrow screen). */}
      <div className="relative flex flex-col gap-4 p-4 sm:p-6 md:hidden">
        <VoiceWaveform
          bars={bars}
          turns={turns}
          activeTurn={activeTurn}
          revealedTurns={revealedTurns}
          className="h-14 w-full"
        />
        <ul className="flex flex-col gap-2.5">
          {turns.map((turn, index) => (
            <li
              key={`${turn.speaker}-${turn.time}`}
              className={clsx(
                "flex",
                turn.isGail ? "justify-end" : "justify-start",
                !isRevealed(index) && "invisible",
              )}
            >
              <VoiceTurnCard
                turn={turn}
                handlers={handlersFor(index)}
                className={clsx(
                  "w-[82%] max-w-xs",
                  turn.isGail ? "origin-bottom-right" : "origin-bottom-left",
                  isRevealed(index) && "animate-message-in",
                )}
              />
            </li>
          ))}
        </ul>
      </div>

      {/* Desktop: cards float along the waveform, alternating top/bottom. */}
      <div className="hidden md:block">
        <VoiceWaveform
          bars={bars}
          turns={turns}
          activeTurn={activeTurn}
          revealedTurns={revealedTurns}
          className="absolute inset-x-0 top-1/2 h-20 w-full -translate-y-1/2"
        />

        {turns.map((turn, index) => {
          const layout = turnLayout[index] ?? turnLayout[0];
          const isTop = layout.side === "top";
          const cardEdge = isTop
            ? WAVE_CENTER - CONNECTOR_REACH
            : WAVE_CENTER + CONNECTOR_REACH;
          // Keep the (center-anchored) card fully inside the visual: never let
          // its left/right edge cross a small gutter, even near the bounds.
          const cardLeft = `clamp(${CARD_HALF_WIDTH}, ${layout.anchorX}%, calc(100% - ${CARD_HALF_WIDTH}))`;

          return (
            // Visibility (not display) keeps absolute children positioned
            // against the visual while hidden, and inherits to all of them.
            <div
              key={`${turn.speaker}-${turn.time}`}
              className={clsx(!isRevealed(index) && "invisible")}
            >
              <span
                className={clsx(
                  "absolute z-0 w-px",
                  turn.isGail ? "bg-gail-primary/55" : "bg-black/25",
                )}
                style={{
                  left: `${layout.anchorX}%`,
                  top: `${isTop ? cardEdge : WAVE_CENTER}%`,
                  height: `${CONNECTOR_REACH}%`,
                }}
              />

              <span
                className={clsx(
                  "absolute z-10 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full ring-2 ring-white/70",
                  turn.isGail ? "bg-gail-primary" : "bg-neutral-900",
                )}
                style={{ left: `${layout.anchorX}%`, top: `${WAVE_CENTER}%` }}
              />

              <div
                className="absolute z-10 -translate-x-1/2"
                style={
                  isTop
                    ? { left: cardLeft, bottom: `${100 - cardEdge}%` }
                    : { left: cardLeft, top: `${cardEdge}%` }
                }
              >
                <VoiceTurnCard
                  turn={turn}
                  handlers={handlersFor(index)}
                  className={clsx(
                    "w-48",
                    isTop ? "origin-bottom" : "origin-top",
                    isRevealed(index) && "animate-message-in",
                  )}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
